import type { Customer, CustomerPayload, PartnerCategory } from '@/api/customersApi'
import {
  formatPostalAddress,
  fromLegacyAddress,
  validatePostalAddress,
  type AddressErrors,
  type AddressType,
  type PostalAddress,
} from '@/lib/address/address-rules'

/**
 * Pure rules for Configure → Partners (z8nrz7earp; Customers in z8nrz7e6zn),
 * kept out of the component so they are unit-tested (vitest here runs `.ts`
 * only).
 */

/**
 * What a customer picker asks for: the partners in the Customer category — "the
 * list of names" of the spec (z8nrz7earp). One constant, so the query key is
 * stable across renders.
 */
export const CUSTOMER_CATEGORY: readonly PartnerCategory[] = ['Customer']

/** A partner's category; a server older than Partners sends none, and every row there is a Customer. */
export function categoryOf(c: Pick<Customer, 'category'>): PartnerCategory {
  return c.category ?? 'Customer'
}

/**
 * Rows whose code, name or contact person contains `q`, case-insensitively, and
 * in `category` when one is picked.
 */
export function filterCustomers(
  rows: readonly Customer[],
  q: string,
  category: PartnerCategory | 'all' = 'all',
): Customer[] {
  const needle = q.trim().toLowerCase()
  return rows.filter(
    c =>
      (category === 'all' || categoryOf(c) === category) &&
      (!needle || [c.code, c.name, c.contactPerson].some(v => v?.toLowerCase().includes(needle))),
  )
}

/**
 * The address part of the form (z8nrz7epeq): every field as the text in its
 * input. `state` holds whatever the state control has — a code picked from the
 * country's list, or a name typed where the country has none.
 */
export interface AddressDraft {
  addressType: AddressType
  companyName: string
  attentionTo: string
  addressLine1: string
  addressLine2: string
  addressLine3: string
  city: string
  districtLocality: string
  state: string
  postalCode: string
  countryCode: string
}

/** What the partner dialog edits. */
export interface CustomerDraft {
  code: string
  name: string
  category: PartnerCategory | ''
  contactPerson: string
  phone: string
  email: string
  address: AddressDraft
}

export type CustomerFormErrors = Partial<Record<'code' | 'name' | 'category', string>> & {
  address?: AddressErrors
}

/** An address draft from a partner's address; blank, in `fallbackCountry`, when it has none. */
export function addressDraftFrom(a?: PostalAddress | null, fallbackCountry = ''): AddressDraft {
  return {
    addressType: a?.addressType ?? 'Shipping',
    companyName: a?.companyName ?? '',
    attentionTo: a?.attentionTo ?? '',
    addressLine1: a?.addressLine1 ?? '',
    addressLine2: a?.addressLine2 ?? '',
    addressLine3: a?.addressLine3 ?? '',
    city: a?.city ?? '',
    districtLocality: a?.districtLocality ?? '',
    state: a?.stateProvinceCode ?? a?.stateProvinceRegion ?? '',
    postalCode: a?.postalCode ?? '',
    countryCode: a?.countryCode ?? fallbackCountry,
  }
}

/** Nothing typed: a country on its own (the remembered default) is not an address. */
export function isAddressBlank(a: AddressDraft): boolean {
  return [
    a.companyName,
    a.attentionTo,
    a.addressLine1,
    a.addressLine2,
    a.addressLine3,
    a.city,
    a.districtLocality,
    a.state,
    a.postalCode,
  ].every(v => !v.trim())
}

/** The draft as an address for the shared rules. */
export function toPostalAddress(a: AddressDraft): PostalAddress {
  return {
    addressType: a.addressType,
    companyName: a.companyName,
    attentionTo: a.attentionTo,
    addressLine1: a.addressLine1,
    addressLine2: a.addressLine2,
    addressLine3: a.addressLine3,
    city: a.city,
    districtLocality: a.districtLocality,
    // Matched to the country's list by code or name; free text where there is none.
    stateProvinceCode: a.state,
    postalCode: a.postalCode,
    countryCode: a.countryCode,
  }
}

/**
 * Field errors for a draft; empty when it can be saved. Code, name and the
 * category are required. The address is optional, but one that is entered must
 * fit its country: checked only when `checkAddress` (the user changed it, or it
 * is a new partner's), so an old address that needs review never blocks an
 * edit of the phone number.
 */
export function validateCustomer(draft: CustomerDraft, { checkAddress = true } = {}): CustomerFormErrors {
  const errors: CustomerFormErrors = {}
  if (!draft.code.trim()) errors.code = 'Code is required.'
  if (!draft.name.trim()) errors.name = 'Name is required.'
  if (!draft.category) errors.category = 'Pick a category.'
  if (checkAddress && !isAddressBlank(draft.address)) {
    const address = validatePostalAddress(toPostalAddress(draft.address)).errors
    if (Object.keys(address).length > 0) errors.address = address
  }
  return errors
}

/**
 * The body to save. The address goes only when `includeAddress` (it was
 * changed, or the partner is new): as the shared rules normalised it, or null
 * when it was cleared. Left out, the server keeps what it has.
 */
export function customerPayload(draft: CustomerDraft, { includeAddress = true } = {}): CustomerPayload {
  return {
    code: draft.code.trim(),
    name: draft.name.trim(),
    category: draft.category,
    contactPerson: draft.contactPerson.trim(),
    phone: draft.phone.trim(),
    email: draft.email.trim(),
    ...(includeAddress
      ? {
          postalAddress: isAddressBlank(draft.address)
            ? null
            : validatePostalAddress(toPostalAddress(draft.address)).value,
        }
      : {}),
  }
}

/** The dialog's starting values: the partner being edited, or blanks with no category picked. */
export function customerDraft(c?: Customer | null, fallbackCountry = ''): CustomerDraft {
  return {
    code: c?.code ?? '',
    name: c?.name ?? '',
    category: c ? categoryOf(c) : '',
    contactPerson: c?.contactPerson ?? '',
    phone: c?.phone ?? '',
    email: c?.email ?? '',
    address: addressDraftFrom(c?.postalAddress ?? (c ? fromLegacyAddress(c) : null), fallbackCountry),
  }
}

/** What the Partners table shows of an address (z8nrz7epeq). */
export interface PartnerAddressCell {
  /** At most two lines: the street, then the rest as the country writes it (`Tampa, FL 33602, United States`). */
  lines: string[]
  /** The whole address on one line, for sorting and the cell's tooltip. */
  text: string
  needsReview: boolean
}

/**
 * A partner's address for the table's one Address column: from the structured
 * address, and from the flat fields when the server sends none (an older one).
 * A country the old free text could not be matched to stays as it was typed,
 * flagged for review.
 */
export function partnerAddressCell(c: Customer): PartnerAddressCell {
  const a = c.postalAddress === undefined ? fromLegacyAddress(c) : c.postalAddress
  if (!a) return { lines: [], text: '', needsReview: false }
  // The partner's name is its own column, so neither name line is repeated here.
  const formatted = formatPostalAddress({ ...a, companyName: null, attentionTo: null })
  if (!a.countryCode && c.country?.trim()) formatted.push(c.country.trim())
  const [first = '', ...rest] = formatted
  const lines = [first, rest.join(', ')].filter(Boolean)
  return {
    lines,
    text: lines.join(', '),
    needsReview: c.postalAddress ? c.postalAddress.needsReview : false,
  }
}
